// src/client/components/Navbar/index.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import { queryStringToObject } from 'client/helpers/queryStringToObject';

interface IProps {
  navList: IData[];
}
interface IData {
  title: string;
  link: string;
  list?: IData[];
}

// --- helpers sûrs pour SSR
const safeGetToken = () => {
  if (typeof window === 'undefined') return null; // SSR -> rien
  try {
    return localStorage.getItem('token_b2b');
  } catch {
    return null;
  }
};
const safeRemoveToken = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('token_b2b');
    sessionStorage.clear();
  } catch {}
};

const Navbar: React.FC<IProps> = ({ navList }) => {
  const location = useLocation();
  const history = useHistory();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  // ⚠️ Initialise à false côté serveur; on synchronisera côté client
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Synchronisation côté client seulement
  useEffect(() => {
    // première synchro après hydration
    setIsLoggedIn(Boolean(safeGetToken()));

    // réagit aux changements du storage (autre onglet)
    const onStorage = () => setIsLoggedIn(Boolean(safeGetToken()));
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
  }, []);

  // réévalue après navigation (ex: on vient de /b2b-login)
  useEffect(() => {
    setIsLoggedIn(Boolean(safeGetToken()));
  }, [location.pathname, location.search]);

  const getIsCurrentLink = useCallback(
    (str: string) =>
      str === `${location.pathname}${location.search || ''}` ||
      (location.search &&
        queryStringToObject(location.search)['product-type'] &&
        queryStringToObject(str)['product-type'] &&
        queryStringToObject(location.search)['product-type'] ===
          queryStringToObject(str)['product-type']),
    [location.pathname, location.search],
  );

  const handleMyReservation = () => {
    if (safeGetToken()) history.push('/mes-reservations');
    else history.push('/checking-reservation-details');
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setLogoutError(null);

    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e: any) {
      // la route peut ne pas exister en dev
      setLogoutError('Déconnexion locale effectuée.');
    } finally {
      safeRemoveToken();
      setIsLoggedIn(false);
      setIsLoggingOut(false);
      history.replace('/b2b-login');
    }
  };

  const espaceB2bHref = isLoggedIn ? '/catalogue-b2b' : '/espace-b2b';

  return (
    <nav className="navbar navbar-expand-lg text-primary align-items-center justify-content-start bg-white">
      <ul style={{ listStyle: 'none' }} className="d-flex align-content-center mb-0 pl-0">
        {navList.map((e) => (
          <li
            key={e.title}
            title={e.title.toLowerCase()}
            className={`h4 mx-2 my-auto ${
              e.list ? 'position-relative dropdown-toggle dropdown-title' : ''
            }`}
          >
            {getIsCurrentLink(e.link) ? (
              <u>
                <Link to={e.link}>{e.title}</Link>
                {e.list && (
                  <ul className="h4 dropdown-menu px-2 rounded">
                    {e.list.map((el) => (
                      <Link key={el.title} to={el.link}>
                        <span className="py-2 text-underline">{el.title}</span>
                      </Link>
                    ))}
                  </ul>
                )}
              </u>
            ) : (
              <>
                <Link to={e.link}>{e.title}</Link>
                {e.list && (
                  <ul className="h4 dropdown-menu px-2 rounded">
                    {e.list.map((el) => (
                      <Link key={el.title} to={el.link}>
                        <span className="py-2 text-underline">{el.title}</span>
                      </Link>
                    ))}
                  </ul>
                )}
              </>
            )}
          </li>
        ))}

        {/* Espace B2B */}
        <li title="Espace B2B" className="h4 mx-2 my-auto">
          <Link to={espaceB2bHref}>
            <button className="btn btn-primary border-0" type="button">
              Espace B2B
            </button>
          </Link>
        </li>

        {/* Mes réservations */}
        <li title="Mes réservations" className="h4 mx-2 my-auto">
          <button className="btn btn-primary border-0" onClick={handleMyReservation}>
            Ma réservation
          </button>
        </li>

        {/* Déconnexion visible que si connecté */}
        {isLoggedIn && (
          <li title="Déconnexion" className="h4 mx-2 my-auto">
            <button
              className="btn btn-primary border-0 d-inline-flex align-items-center"
              style={{ backgroundColor: '#f56371', opacity: isLoggingOut ? 0.8 : 1 }}
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut && (
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: 8 }}
                />
              )}
              {isLoggingOut ? 'Déconnexion…' : 'Déconnexion'}
            </button>
          </li>
        )}

        {logoutError && <small className="text-danger ml-2">{logoutError}</small>}
      </ul>
    </nav>
  );
};

export default Navbar;
